import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getFavoriteListingIds } from '../../util/api';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';

// The signed-in member's Favourites, one page at a time.
//
// The saved ids come from MongoDB (server/api/misc/favoriteListings.js), newest
// first, and the listings themselves from Sharetribe.
//
// PAGES ARE CUT FROM LIVE LISTINGS ONLY
//   A saved listing that was later closed or deleted keeps its row, but
//   Sharetribe no longer returns it. Cutting pages from the raw ids would leave
//   such pages short, or empty with more pages after them. So the ids are first
//   checked against Sharetribe (a light query, titles only), and the pages are
//   cut from the ones still live.
//
// THE PAGE IS A SNAPSHOT
//   Removing a favourite here empties its heart (that is the favoriteListings
//   duck) but leaves the card where it is until the page loads again, so a
//   mistaken tap can be undone with a second one.

export const RESULT_PAGE_SIZE = 10;

// Sharetribe takes at most 100 ids in one listings query.
const IDS_PER_QUERY = 100;

// ================ Action types ================ //

export const FETCH_FAVORITES_PAGE_REQUEST = 'app/FavoriteListingsPage/FETCH_FAVORITES_PAGE_REQUEST';
export const FETCH_FAVORITES_PAGE_SUCCESS = 'app/FavoriteListingsPage/FETCH_FAVORITES_PAGE_SUCCESS';
export const FETCH_FAVORITES_PAGE_ERROR = 'app/FavoriteListingsPage/FETCH_FAVORITES_PAGE_ERROR';

// ================ Reducer ================ //

const initialState = {
  // The page asked for last. A slower answer for an earlier page is dropped.
  requestedPage: null,
  queryInProgress: false,
  queryError: null,
  currentPageResultIds: [],
  pagination: null,
};

const favoriteListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_FAVORITES_PAGE_REQUEST:
      return {
        ...state,
        requestedPage: payload.page,
        queryInProgress: true,
        queryError: null,
      };
    case FETCH_FAVORITES_PAGE_SUCCESS:
      if (payload.pagination.page !== state.requestedPage) {
        return state;
      }
      return {
        ...state,
        queryInProgress: false,
        currentPageResultIds: payload.resultIds,
        pagination: payload.pagination,
      };
    case FETCH_FAVORITES_PAGE_ERROR:
      if (payload.page !== state.requestedPage) {
        return state;
      }
      return { ...state, queryInProgress: false, queryError: payload.error };

    default:
      return state;
  }
};

export default favoriteListingsPageReducer;

// ================ Action creators ================ //

export const fetchFavoritesPageRequest = page => ({
  type: FETCH_FAVORITES_PAGE_REQUEST,
  payload: { page },
});

export const fetchFavoritesPageSuccess = (resultIds, pagination) => ({
  type: FETCH_FAVORITES_PAGE_SUCCESS,
  payload: { resultIds, pagination },
});

export const fetchFavoritesPageError = (page, error) => ({
  type: FETCH_FAVORITES_PAGE_ERROR,
  error: true,
  payload: { page, error },
});

// ================ Thunks ================ //

const inBatches = (ids, size) => {
  const batches = [];
  for (let i = 0; i < ids.length; i += size) {
    batches.push(ids.slice(i, i + size));
  }
  return batches;
};

// The saved ids Sharetribe still returns, kept in saved order.
const liveListingIds = (ids, sdk) =>
  Promise.all(
    inBatches(ids, IDS_PER_QUERY).map(batch =>
      sdk.listings.query({ ids: batch, perPage: IDS_PER_QUERY, 'fields.listing': ['title'] })
    )
  ).then(responses => {
    const live = new Set();
    responses.forEach(response => response.data.data.forEach(l => live.add(l.id.uuid)));
    return ids.filter(id => live.has(id));
  });

// The same listing and author fields the search page asks for, so ListingCard
// has what it needs.
// Listings have no pictures on Parnexus, so images are not requested.
const pageQueryParams = (ids, config) => {
  return {
    ids,
    perPage: ids.length,
    include: ['author'],
    'fields.listing': ['title', 'description', 'geolocation', 'price', 'publicData'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
  };
};

export const fetchFavoritesPage = (page, config) => (dispatch, getState, sdk) => {
  dispatch(fetchFavoritesPageRequest(page));

  return getFavoriteListingIds()
    .then(response => liveListingIds(response?.listingIds || [], sdk))
    .then(liveIds => {
      const totalItems = liveIds.length;
      const pagination = {
        page,
        perPage: RESULT_PAGE_SIZE,
        totalItems,
        totalPages: Math.ceil(totalItems / RESULT_PAGE_SIZE),
      };

      const start = (page - 1) * RESULT_PAGE_SIZE;
      const pageIds = liveIds.slice(start, start + RESULT_PAGE_SIZE);
      if (pageIds.length === 0) {
        dispatch(fetchFavoritesPageSuccess([], pagination));
        return;
      }

      return sdk.listings.query(pageQueryParams(pageIds, config)).then(response => {
        const listingFields = config?.listing?.listingFields;
        dispatch(addMarketplaceEntities(response, { listingFields }));

        // Sharetribe returns them in its own order; put them back in saved
        // order. One closed in the moment since the check is just left out.
        const returned = new Map(response.data.data.map(l => [l.id.uuid, l.id]));
        const resultIds = pageIds.filter(id => returned.has(id)).map(id => returned.get(id));
        dispatch(fetchFavoritesPageSuccess(resultIds, pagination));
      });
    })
    .catch(e => {
      dispatch(fetchFavoritesPageError(page, storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => dispatch => {
  // The favourites API needs window.fetch and the session cookie, so this only
  // runs in the browser. Routes.js calls loadData again once the page mounts.
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  const page = Math.max(1, parseInt(parse(search).page, 10) || 1);
  return dispatch(fetchFavoritesPage(page, config));
};
